import type { FC, ReactNode } from 'react'
import type { PlayColor, PlayLevel } from '@/constants/play'

import { useState } from 'react'
import Link from 'next/link'
import { ArrowRight, Cpu, Shuffle, Users } from 'lucide-react'
import { cn } from '@/lib/utils'
import NeonPiece from '@/components/chess/neon-piece'
import { neonButtonClasses } from '@/components/landing/neon-link'
import { useBoardTheme } from '@/components/chess/board-theme'

import {
    DEFAULT_COLOR,
    DEFAULT_LEVEL,
    PLAY_COLORS,
    PLAY_LEVELS,
    playHref
} from '@/constants/play'

const chipBase = 'cursor-pointer rounded-xl border text-sm font-medium transition-all has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-neon-cyan'

const PlaySection: FC = (): ReactNode => {
    const { theme } = useBoardTheme()
    const [level, setLevel] = useState<PlayLevel>(DEFAULT_LEVEL)
    const [color, setColor] = useState<PlayColor>(DEFAULT_COLOR)

    return (
        <section id='play' aria-labelledby='play-title' className='relative scroll-mt-20 px-5 py-24 sm:px-8 sm:py-32'>
            <div className='relative mx-auto max-w-6xl overflow-hidden rounded-[2rem] border border-neon-pink/35 bg-[#0a0718] px-6 py-14 shadow-[0_0_0_1px_rgb(255_61_206/0.12),0_0_80px_rgb(255_61_206/0.18)] sm:px-10 sm:py-16 lg:px-14'>
                <div aria-hidden='true' className='pointer-events-none absolute inset-0'>
                    <div className='absolute -left-24 -top-24 size-80 rounded-full bg-neon-pink/25 blur-[110px]' />
                    <div className='absolute -bottom-32 right-0 size-96 rounded-full bg-neon-cyan/15 blur-[120px]' />
                    <div className='neon-grid-bg absolute inset-0 opacity-30 [mask-image:linear-gradient(to_bottom,transparent,black_40%,transparent)]' />
                    <NeonPiece type='k' hollow strokeWidth={1} className='absolute -right-12 -top-10 hidden size-72 text-neon-pink/25 drop-shadow-[0_0_24px_rgb(255_61_206/0.5)] lg:block' />
                </div>

                <div className='relative max-w-2xl'>
                    <h2 id='play-title' className='font-heading text-4xl font-semibold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl'>
                        Your move.
                        <br />
                        <span className='text-neon-pink glow-text-pink'>Make it glow.</span>
                    </h2>

                    <p className='mt-6 max-w-xl text-lg leading-relaxed text-pretty text-muted-foreground'>
                        Pick a level, choose your color and start playing in seconds. No downloads, no account, no waiting room.
                    </p>
                </div>

                <div className='relative mt-12 grid gap-5 lg:grid-cols-[1.45fr_1fr]'>
                    <article className='flex flex-col rounded-3xl border border-neon-cyan/30 bg-[#070b18]/80 p-6 shadow-[0_0_40px_rgb(46_242_255/0.08)] backdrop-blur-sm sm:p-7'>
                        <div className='flex items-start gap-4'>
                            <span className='flex size-12 shrink-0 items-center justify-center rounded-2xl border border-neon-cyan/40 bg-neon-cyan/10 text-neon-cyan shadow-[0_0_20px_rgb(46_242_255/0.25)]'>
                                <Cpu className='size-6' />
                            </span>

                            <div>
                                <h3 className='font-heading text-xl font-semibold tracking-tight'>
                                    Play the computer
                                </h3>

                                <p className='mt-1.5 text-sm leading-relaxed text-muted-foreground'>
                                    Three levels, from friendly to ruthless. It answers in a heartbeat.
                                </p>
                            </div>
                        </div>

                        <div className='mt-6 grid gap-5 sm:grid-cols-2'>
                            <fieldset>
                                <legend className='font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground'>
                                    Level
                                </legend>

                                <div className='mt-2.5 grid grid-cols-3 gap-2'>
                                    {PLAY_LEVELS.map(option => {
                                        const active = option.id === level

                                        return (
                                            <label
                                                key={option.id}
                                                title={option.hint}
                                                className={cn(
                                                    chipBase,
                                                    'px-2 py-2.5 text-center',
                                                    active ? 'border-neon-pink/70 bg-neon-pink/10 text-neon-pink shadow-[0_0_18px_rgb(255_61_206/0.25)]' : 'border-white/15 text-foreground/80 hover:border-white/30'
                                                )}
                                            >
                                                <input type='radio' name='landing-level' value={option.id} checked={active} onChange={() => setLevel(option.id)} className='sr-only' />
                                                {option.label}
                                            </label>
                                        )
                                    })}
                                </div>

                                <p className='mt-2 text-xs text-muted-foreground'>
                                    {PLAY_LEVELS.find(option => option.id === level)?.hint}
                                </p>
                            </fieldset>

                            <fieldset>
                                <legend className='font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground'>
                                    Your color
                                </legend>

                                <div className='mt-2.5 grid grid-cols-3 gap-2'>
                                    {PLAY_COLORS.map(option => {
                                        const active = option.id === color

                                        return (
                                            <label
                                                key={option.id}
                                                className={cn(
                                                    chipBase,
                                                    'flex flex-col items-center gap-1 px-2 py-2',
                                                    active ? 'border-white/60 bg-white/5 text-foreground' : 'border-white/15 text-foreground/80 hover:border-white/30'
                                                )}
                                            >
                                                <input type='radio' name='landing-color' value={option.id} checked={active} onChange={() => setColor(option.id)} className='sr-only' />

                                                {option.id === 'random' ? (
                                                    <Shuffle className='size-6 text-neon-violet' />
                                                ) : (
                                                    <span
                                                        className='transition-colors duration-500'
                                                        style={{
                                                            color: option.id === 'white' ? theme.white : theme.black,
                                                            filter: `drop-shadow(0 0 5px ${option.id === 'white' ? theme.white : theme.black}99)`
                                                        }}
                                                    >
                                                        <NeonPiece type='k' strokeWidth={2} className='size-6' />
                                                    </span>
                                                )}

                                                <span className='text-xs'>
                                                    {option.label}
                                                </span>
                                            </label>
                                        )
                                    })}
                                </div>
                            </fieldset>
                        </div>

                        <Link href={playHref('computer', level, color)} className={cn(neonButtonClasses('primary', 'lg'), 'mt-7 w-full')}>
                            Play the computer
                            <ArrowRight className='size-5' />
                        </Link>
                    </article>

                    <article className='flex flex-col rounded-3xl border border-neon-pink/30 bg-[#12071a]/80 p-6 shadow-[0_0_40px_rgb(255_61_206/0.08)] backdrop-blur-sm sm:p-7'>
                        <div className='flex items-start gap-4'>
                            <span className='flex size-12 shrink-0 items-center justify-center rounded-2xl border border-neon-pink/40 bg-neon-pink/10 text-neon-pink shadow-[0_0_20px_rgb(255_61_206/0.25)]'>
                                <Users className='size-6' />
                            </span>

                            <div>
                                <h3 className='font-heading text-xl font-semibold tracking-tight'>
                                    Pass and play
                                </h3>

                                <p className='mt-1.5 text-sm leading-relaxed text-muted-foreground'>
                                    Two players, one screen. The board turns after every move so each of you plays from your own side.
                                </p>
                            </div>
                        </div>

                        <div aria-hidden='true' className='my-6 flex flex-1 items-center justify-center gap-5'>
                            <span className='transition-colors duration-500' style={{ color: theme.white, filter: `drop-shadow(0 0 10px ${theme.white}aa)` }}>
                                <NeonPiece type='k' className='size-16' />
                            </span>

                            <span className='font-heading text-sm font-semibold uppercase tracking-[0.3em] text-muted-foreground'>
                                vs
                            </span>

                            <span className='transition-colors duration-500' style={{ color: theme.black, filter: `drop-shadow(0 0 10px ${theme.black}aa)` }}>
                                <NeonPiece type='k' className='size-16' />
                            </span>
                        </div>

                        <Link href={playHref('friend')} className={cn(neonButtonClasses('outline', 'lg'), 'w-full')}>
                            Pass and play
                            <ArrowRight className='size-5' />
                        </Link>
                    </article>
                </div>
            </div>
        </section>
    )
}

export default PlaySection
